"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import { Globe, MapPin, Store } from "lucide-react";
import { useCardAvailability } from "modules/cards/hooks/useCardAvailability";
import { useCardCities } from "modules/cards/hooks/useCardCities";
import { useClientReverseGeocode } from "modules/cards/hooks/useClientReverseGeocode";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "modules/common/components/ui/tabs";
import {
  AvailabilityCardSkeleton,
  CityListItem,
  CityWithLocation,
} from "./CityListItem";
import { MergedStore, StoreListItem } from "./StoreListItem";
import { LGS } from "modules/lgs/model/lgs";
import { SignInButton } from "@clerk/nextjs";
import { SignUpButtonCTA } from "modules/auth/components/SignUpButtonCTA";
import { useAppUser } from "modules/auth/user/hooks/useAppUser";

interface CardAvailabilitySectionClientProps {
  game: string;
  setCode: string;
  cardNumber: string;
  cardId: string;
  cardName: string;
  isAuthenticated?: boolean;
  hasStores?: boolean;
}

/**
 * Stable identity for a city row. Client-geocoded entries start as coordinates
 * and become a named city once they resolve, so this is also the React key:
 * the list is deduped and re-sorted on every render, and an index key would
 * hand a resolving card the previous row's DOM.
 */
function cityKey(city: CityWithLocation) {
  return city.isLoading || !city.city
    ? `coords:${city.latitude.toFixed(6)},${city.longitude.toFixed(6)}`
    : `city:${city.city.toLowerCase()}|${(city.country || "").toLowerCase()}`;
}

/** Merge available and wanted LGS arrays into a single array with counts */
function mergeStoreLists(
  available: LGS[] = [],
  wanted: LGS[] = []
): MergedStore[] {
  const storeMap = new Map<number, MergedStore>();

  // Add available stores
  for (const store of available) {
    const existing = storeMap.get(store.wotcId);
    if (existing) {
      existing.availableCount += 1;
    } else {
      storeMap.set(store.wotcId, {
        wotcId: store.wotcId,
        slug: store.slug,
        name: store.name,
        postalAddress: store.postalAddress,
        isPremium: store.isPremium,
        isTradingHub: store.isTradingHub,
        availableCount: 1,
        wantedCount: 0,
      });
    }
  }

  // Add wanted stores
  for (const store of wanted) {
    const existing = storeMap.get(store.wotcId);
    if (existing) {
      existing.wantedCount += 1;
    } else {
      storeMap.set(store.wotcId, {
        wotcId: store.wotcId,
        slug: store.slug,
        name: store.name,
        postalAddress: store.postalAddress,
        isPremium: store.isPremium,
        isTradingHub: store.isTradingHub,
        availableCount: 0,
        wantedCount: 1,
      });
    }
  }

  return Array.from(storeMap.values());
}

export function CardAvailabilitySectionClient({
  game,
  setCode,
  cardNumber,
  cardId,
  cardName,
  isAuthenticated: isAuthenticatedProp,
  hasStores: hasStoresProp,
}: CardAvailabilitySectionClientProps) {
  const [hasOpenedStores, setHasOpenedStores] = useState(false);

  // Derive auth/stores state client-side when not provided by server
  const { appUser, isUserLoaded } = useAppUser();
  const isAuthenticated = isAuthenticatedProp ?? (isUserLoaded && !!appUser);
  const hasStores = hasStoresProp ?? (!!appUser?.lgs && appUser.lgs.length > 0);

  // The viewed game fully determines this public aggregate. Every mount checks
  // the short shared edge entry without sending viewer credentials.
  const { cities, isLoading: citiesLoading } = useCardCities(
    game,
    setCode,
    cardNumber,
    cardId
  );

  // Cities with backend-provided names; fall back to client geocoding for pre-backfill records
  const citiesNeedingGeocode = useMemo(
    () => cities.filter((c) => !c.cityName),
    [cities]
  );
  const geocodedCities = useClientReverseGeocode(citiesNeedingGeocode);

  const citiesWithNames: CityWithLocation[] = useMemo(() => {
    const backendNamed: CityWithLocation[] = cities
      .filter((c) => c.cityName)
      .map((c) => ({
        ...c,
        cityName: c.country ? `${c.cityName}, ${c.country}` : c.cityName,
        city: c.cityName,
        country: c.country,
        isLoading: false,
      }));

    const combined = [...backendNamed, ...geocodedCities];

    // Deduplicate: merge entries that resolved to the same city
    const deduped = new Map<string, CityWithLocation>();
    for (const city of combined) {
      const key = cityKey(city);
      const existing = deduped.get(key);
      if (existing) {
        // Same city resolved from multiple backend cells: items can overlap
        // (one user, stores in multiple cells), so taking the max is a safe
        // lower bound. Summing would inflate when items overlap.
        existing.availableCount = Math.max(
          existing.availableCount,
          city.availableCount
        );
        existing.wantedCount = Math.max(existing.wantedCount, city.wantedCount);
        existing.storeIds = [
          ...new Set([...existing.storeIds, ...city.storeIds]),
        ];
        existing.storeCount = existing.storeIds.length;
      } else {
        deduped.set(key, { ...city });
      }
    }

    return [...deduped.values()];
  }, [cities, geocodedCities]);

  // Store availability data - only fetched if authenticated with stores
  const { availability, query: availabilityQuery } = useCardAvailability({
    setCode,
    number: cardNumber,
    cardId,
    isAuthenticated,
    hasStores,
    shouldFetch: hasOpenedStores,
  });

  // Merge available and wanted stores into single list
  const mergedStores = useMemo(() => {
    if (!availability) return [];
    return mergeStoreLists(availability.available, availability.wanted);
  }, [availability]);

  const cityCount = citiesWithNames.length;
  const storeCount = mergedStores.length;

  return (
    <section
      className="flex flex-col px-2 md:px-0"
      aria-label="Card availability"
    >
      <div className="mb-4">
        <h2 className="flex items-center gap-2 text-lg font-bold">
          <MapPin size={ICON_SIZE} className="text-primary" />
          Card Availability
        </h2>
        <p className="text-base-content/70 mt-1 text-sm">
          See where <strong>{cardName}</strong> is available worldwide and at
          your local stores
        </p>
      </div>
      <Tabs
        defaultValue="cities"
        variant="boxed"
        onValueChange={(value) => {
          if (value === "stores") setHasOpenedStores(true);
        }}
      >
        <TabsList className="mb-2 w-fit">
          <TabsTrigger
            value="cities"
            icon={<Globe size={ICON_SIZE} />}
            badge={cityCount > 0 ? cityCount : undefined}
          >
            Cities
          </TabsTrigger>
          <TabsTrigger
            value="stores"
            icon={<Store size={ICON_SIZE} />}
            badge={storeCount > 0 ? storeCount : undefined}
          >
            Your Stores
          </TabsTrigger>
        </TabsList>

        {/* Cities Tab */}
        <TabsContent value="cities">
          <CitiesTabContent
            cities={citiesWithNames}
            cardName={cardName}
            isLoading={citiesLoading}
          />
        </TabsContent>

        {/* Stores Tab */}
        <TabsContent value="stores">
          <StoresTabContent
            stores={mergedStores}
            cardName={cardName}
            isAuthenticated={isAuthenticated}
            hasStores={hasStores}
            isLoading={availabilityQuery.isLoading}
          />
        </TabsContent>
      </Tabs>
    </section>
  );
}

interface CitiesTabContentProps {
  cities: CityWithLocation[];
  cardName: string;
  isLoading: boolean;
}

function CitiesTabContent({
  cities,
  cardName,
  isLoading,
}: CitiesTabContentProps) {
  if (isLoading) {
    return <AvailabilityGridSkeleton />;
  }

  if (cities.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <MapPin size={32} className="text-base-content/40 mb-2" />
        <h3 className="text-lg font-bold">No Cities Found</h3>
        <p className="text-base-content/70 max-w-md">
          <strong>{cardName}</strong> is not currently available for trade in
          any city worldwide.
        </p>
      </div>
    );
  }

  // Sort cities by total activity (available + wanted)
  const sortedCities = [...cities].sort(
    (a, b) =>
      b.availableCount + b.wantedCount - (a.availableCount + a.wantedCount)
  );

  return (
    <div
      className="max-h-[500px] overflow-y-auto overscroll-y-contain pr-1"
      role="region"
      aria-label="Cities with this card"
      tabIndex={0}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sortedCities.map((city) => (
          <CityListItem key={cityKey(city)} city={city} cardName={cardName} />
        ))}
      </div>
    </div>
  );
}

interface StoresTabContentProps {
  stores: MergedStore[];
  cardName: string;
  isAuthenticated: boolean;
  hasStores: boolean;
  isLoading: boolean;
}

function StoresTabContent({
  stores,
  cardName,
  isAuthenticated,
  hasStores,
  isLoading,
}: StoresTabContentProps) {
  // Not authenticated - show sign in CTA
  if (!isAuthenticated) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Store size={32} className="text-primary mb-2" />
        <h3 className="text-lg font-bold">
          Find <span className="text-primary">{cardName}</span> at your local
          game store
        </h3>
        <p className="text-base-content/70 max-w-md">
          Sign in to see if <strong>{cardName}</strong> is available at the LGS
          you play at
        </p>
        <div className="mt-4 flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
          <SignInButton mode="modal">
            <button className="btn btn-primary">Sign In</button>
          </SignInButton>
          <SignUpButtonCTA
            size="md"
            className="btn-secondary"
            variant="navbar"
          />
        </div>
      </div>
    );
  }

  // Authenticated but no stores configured
  if (!hasStores) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <MapPin size={32} className="text-warning mb-2" />
        <h3 className="text-lg font-bold">
          Configure your stores to see card availability
        </h3>
        <p className="text-base-content/70 max-w-md">
          Set up your local game stores to discover where{" "}
          <strong>{cardName}</strong> is available for trade
        </p>
        <div className="mt-4">
          <Link href="/x/settings" className="btn btn-primary">
            Configure Stores
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <AvailabilityGridSkeleton />;
  }

  // Has stores but no availability data
  if (stores.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-8 text-center">
        <Store size={32} className="text-base-content/40 mb-2" />
        <h3 className="text-lg font-bold">Not at Your Stores</h3>
        <p className="text-base-content/70 max-w-md">
          <strong>{cardName}</strong> is not currently available or wanted at
          any of your configured stores.
        </p>
      </div>
    );
  }

  // Sort stores by total activity (available + wanted)
  const sortedStores = [...stores].sort(
    (a, b) =>
      b.availableCount + b.wantedCount - (a.availableCount + a.wantedCount)
  );

  // Show store list
  return (
    <div
      className="max-h-[500px] overflow-y-auto overscroll-contain pr-1"
      role="region"
      aria-label="Stores with this card"
      tabIndex={0}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {sortedStores.map((store) => (
          <StoreListItem key={store.wotcId} store={store} cardName={cardName} />
        ))}
      </div>
    </div>
  );
}

function AvailabilityGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {[...Array(6)].map((_, i) => (
        <AvailabilityCardSkeleton key={i} />
      ))}
    </div>
  );
}
